import {
  Autocomplete,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useRef, useState } from "react";

import { useMutation, useQuery } from "@apollo/client/react";
import { debounce } from "lodash/fp";
import OverwatchModal from "../OverwatchModal";
import TimeScheduleSection from "../TimeScheduleSection";
import { filter, find, map } from "lodash";
import { ScheduleDataType } from "./types";
import { GetSchedulesQuery, GetCompanyResourcesQuery, GetCompanyResourcesDocument, UpdateScheduleDocument, GetSchedulesDocument, CompanyResourceResourceTypeEnum, Address } from "@/graphql/generated/graphql";
import { getInUTC } from "@/utils/dateTimeHelpers";
import { formatAddress, arrayLength } from "@/utils/helperFunctions";
import OverwatchResourceSelector from "../OverwatchResourceSelector";

dayjs.extend(utc);
const normalDebounce = debounce(500);

interface EditSCheduleModalProps {
  handleClose: () => void;
  selectedSchedule: NonNullable<GetSchedulesQuery['getSchedules']>[0]
}

function optionsFormatter(optionsArray: GetCompanyResourcesQuery["getCompanyResources"], resource: string) {
  const data = map(filter(optionsArray, (opt) => opt.resourceType === resource), (option) => ({
    id: option.id,
    name: option.name,
  }));

  return data;
}

const EditScheduleModal: React.FC<EditSCheduleModalProps> = ({ handleClose, selectedSchedule }) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [scheduleData, setScheduleData] = useState<ScheduleDataType>({
    startTime: dayjs(selectedSchedule.startTime),
    endTime: dayjs(selectedSchedule.endTime),
    addressId: selectedSchedule.address.id,
    source: selectedSchedule.source || '',
    title: selectedSchedule.title || '',
    descriptionOfService: selectedSchedule.descriptionOfService || ''
  });

  const [selectedResource, setSelectedResource] = useState<{ id: string, name: string, lastName?: string }>({
    id: selectedSchedule.companyResource?.id as string,
    name: selectedSchedule.companyResource?.name as string,
    lastName: "",
  });

  const disableSubmit = () => {
    return scheduleData.endTime.format('hh:mm a') === dayjs(selectedSchedule.endTime).format('hh:mm a')
      && scheduleData.startTime.format('hh:mm a') === dayjs(selectedSchedule.startTime).format('hh:mm a')
      && (scheduleData.source === selectedSchedule.source || scheduleData.source === '')
      && (scheduleData.title === selectedSchedule.title || scheduleData.title === '')
      && (scheduleData.addressId === selectedSchedule.address.id)
      && (selectedSchedule.companyResource?.id === selectedResource.id)
  };

  const { data: companyResources } = useQuery(GetCompanyResourcesDocument);

  const [update] = useMutation(UpdateScheduleDocument);

  const handleSave = () => {
    update({
      variables: {
        schedule: {
          id: selectedSchedule.id,
          ...((scheduleData.title !== '' && scheduleData.title !== selectedSchedule.title) && { title: scheduleData.title }),
          ...((scheduleData.source !== '' && scheduleData.source !== selectedSchedule.source) && { source: scheduleData.source }),
          ...(getInUTC(scheduleData.startTime) !== selectedSchedule.startTime && { startTime: getInUTC(scheduleData.startTime) }),
          ...(getInUTC(scheduleData.endTime) !== selectedSchedule.endTime && { endTime: getInUTC(scheduleData.endTime) }),
          ...(selectedSchedule.address.id !== scheduleData.addressId && { addressId: scheduleData.addressId }),
          ...(selectedSchedule.companyResource?.id !== selectedResource.id && { companyResourceId: selectedResource.id }),
          addressId: scheduleData.addressId
        }
      },
      onCompleted: handleClose,
      update(cache, { data }) {
        const updatedSchedule = data?.updateSchedule;
        if (!updatedSchedule) return;

        const variables = {
          projectId: selectedSchedule.project.id,
          date: selectedSchedule.date,
          resourceType,
        };
        const existingSchedules =
          cache.readQuery({
            query: GetSchedulesDocument,
            variables,
          })?.getSchedules ?? [];

        cache.writeQuery({
          query: GetSchedulesDocument,
          variables,
          data: {
            getSchedules: [
              ...filter(
                existingSchedules,
                (schedule) => schedule.id !== selectedSchedule.id
              ),
              updatedSchedule,
            ],
          },
        });
      },
    });
  }

  const handleScheduleTimeChange = (field: 'startTime' | 'endTime', value: Dayjs | null) => {
    setScheduleData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resourceType = selectedSchedule.companyResource?.resourceType;
  const autoCompleteType = resourceType === CompanyResourceResourceTypeEnum.Human ? "Employee" : resourceType === CompanyResourceResourceTypeEnum.Equipment ? "Equipment" : "Contractor";

  const projectLocations = [
    selectedSchedule.project.mainAddress as Address,
    ...(selectedSchedule.project?.subLocations as Address[] || [])
  ];

  return (
    <OverwatchModal
      title="Edit Schedule Details"
      primaryButtonText="Update"
      size="sm"
      primaryButtonAction={handleSave}
      disablePrimary={disableSubmit()}
      handleClose={handleClose}
      secondaryButtonText="Cancel"
      secondaryButtonAction={() => handleClose()}
    >
      <Grid container padding={2}>
        <Grid size={12}>
          <OverwatchResourceSelector
            autoCompleteId={`${resourceType}-selector`}
            autoCompleteType={autoCompleteType}
            selection={selectedResource}
            setSelection={setSelectedResource}
            resourceList={optionsFormatter(companyResources?.getCompanyResources || [], resourceType as string)}
          />
        </Grid>
        <Grid size={12}>
          <TimeScheduleSection
            startTime={scheduleData.startTime}
            endTime={scheduleData.endTime}
            onChange={handleScheduleTimeChange}
            size="medium"
          />
        </Grid>
        {resourceType !== CompanyResourceResourceTypeEnum.SubContractor &&
          <Grid container justifyContent='space-between' mt={2} size={12} spacing={2}>
            {resourceType === CompanyResourceResourceTypeEnum.Human &&
              <TextField
                label="Role/Designation (optional)"
                value={scheduleData.title}
                fullWidth
                onChange={(e) => setScheduleData((prev) => ({ ...prev, title: e.target.value }))}
              />
            }
            <TextField
              label="Source (optional)"
              fullWidth
              value={scheduleData.source}
              onChange={(e) => setScheduleData((prev) => ({ ...prev, source: e.target.value }))}
            />
            {resourceType === CompanyResourceResourceTypeEnum.Equipment &&
              <Tooltip title="Future Feature">
                <TextField
                  label="Quantity"
                  variant="outlined"
                  type="number"
                  fullWidth
                  defaultValue={scheduleData.quantity}
                  disabled
                  onChange={(e) => {
                    debounceFunc.cancel();
                    debounceFunc(() => {
                      setScheduleData((prev) => ({
                        ...prev,
                        quantity: parseInt(e.target.value),
                      }));
                    });
                  }}
                />
              </Tooltip>
            }
          </Grid>
        }
      </Grid>

      {resourceType === CompanyResourceResourceTypeEnum.SubContractor &&
        <Grid container justifyContent='space-between' size={12} px={2} mb={2} mt={1}>
          <TextField
            rows={5}
            multiline
            fullWidth
            variant="outlined"
            label="Description of Service"
            defaultValue={scheduleData.descriptionOfService}
            onChange={(e) =>
              setScheduleData(prev => ({
                ...prev,
                descriptionOfService: e.target.value
              }))
            }
          />
        </Grid>
      }
      <Grid container justifyContent='space-between' size={12} px={2}>
        <Autocomplete
          id="address-select"
          options={projectLocations}
          defaultValue={find((projectLocations), location => location.id === selectedSchedule.address.id)}
          disableClearable
          fullWidth
          onChange={(_, newValue) => setScheduleData((prev) => ({ ...prev, addressId: newValue.id }))}
          getOptionLabel={(option) => formatAddress(option as Address)}
          renderInput={(params) => <TextField {...params} label="Location" />}
          readOnly={arrayLength(projectLocations) <= 1}
        />
      </Grid>
    </OverwatchModal>
  );
};

export default EditScheduleModal;
